import { useCallback, useEffect, useRef, useState } from 'react';
import { AssetUploadError, uploadAsset } from '@/api/assets';
import { getApiErrorCode } from '@/lib/api-errors';
import { useBulkCreationStore, type BulkRow } from '@/stores/bulkCreationStore';
import { logger } from '@/utils/logger';

/** Uploads running at the same time; the rest wait in the queue. */
export const MOTIF_UPLOAD_CONCURRENCY = 3;

/** Cell error code when the failure carries no API code. */
export const UPLOAD_FAILED_CODE = 'upload_failed';

/** Cell error code when the server could not read the pixel size. */
export const DIMENSIONS_UNREADABLE_CODE = 'dimensions_unreadable';

/** A cell the store just created, with the local file it stands for. */
export interface MotifUpload {
  cellId: string;
  file: File;
}

function findCellState(rows: readonly BulkRow[], cellId: string) {
  for (const row of rows) {
    if (row.front?.id === cellId) return row.front.state;
    if (row.back?.id === cellId) return row.back.state;
  }
  return undefined;
}

/** The API error code of a failed upload, or the generic fallback. */
export function uploadErrorCode(error: unknown): string {
  if (error instanceof AssetUploadError && error.code) return error.code;
  return getApiErrorCode(error) ?? UPLOAD_FAILED_CODE;
}

async function uploadCell({ cellId, file }: MotifUpload): Promise<void> {
  const { markCellReady, markCellError } = useBulkCreationStore.getState();
  try {
    const asset = await uploadAsset(file);
    // Removed meanwhile: the store ignores unknown cells, so both calls are
    // safe without a check here.
    if (!asset.width || !asset.height) {
      markCellError(cellId, DIMENSIONS_UNREADABLE_CODE);
      return;
    }
    markCellReady(cellId, {
      assetId: asset.id,
      url: asset.url,
      width: asset.width,
      height: asset.height,
    });
  } catch (error) {
    logger.warn('MotifUploads', `Upload of ${file.name} failed`, error);
    markCellError(cellId, uploadErrorCode(error));
  }
}

export interface MotifUploadQueue {
  enqueue: (uploads: readonly MotifUpload[]) => void;
}

/**
 * FIFO queue that runs at most `concurrency` uploads at once and reports
 * each result to the bulk store. A queued cell the seller removed before its
 * turn is skipped, so no bytes go out for it.
 *
 * The queue keeps draining after the motifs step unmounts (the seller may
 * click ahead to step 03 while uploads run): the in-flight promises hold it,
 * and the results land in the store, not in component state.
 */
export function createMotifUploadQueue(
  concurrency: number = MOTIF_UPLOAD_CONCURRENCY
): MotifUploadQueue {
  const pending: MotifUpload[] = [];
  let active = 0;

  const pump = () => {
    while (active < concurrency) {
      const next = pending.shift();
      if (!next) return;
      const state = findCellState(
        useBulkCreationStore.getState().rows,
        next.cellId
      );
      if (state !== 'uploading') continue;
      active += 1;
      void uploadCell(next).finally(() => {
        active -= 1;
        pump();
      });
    }
  };

  return {
    enqueue: (uploads) => {
      pending.push(...uploads);
      pump();
    },
  };
}

export interface UseMotifUploadsResult {
  /** Local files of cells still uploading, for the thumbnail preview. */
  localFiles: ReadonlyMap<string, File>;
  /** Queue the cells `addFiles` just created. */
  startUploads: (uploads: readonly MotifUpload[]) => void;
}

/**
 * Upload runner of step 02, mounted by the flow page (not the step) so the
 * queue and previews outlive a switch to another step: one bounded queue plus the
 * local files of uploading cells. Files are dropped from the map once their
 * cell is ready or removed; the object URLs themselves are owned by
 * `MotifCell`, which revokes them when it stops showing the local preview.
 */
export function useMotifUploads(): UseMotifUploadsResult {
  const queueRef = useRef<MotifUploadQueue | null>(null);
  const [localFiles, setLocalFiles] = useState<ReadonlyMap<string, File>>(
    () => new Map()
  );
  const rows = useBulkCreationStore((state) => state.rows);

  const startUploads = useCallback((uploads: readonly MotifUpload[]) => {
    if (uploads.length === 0) return;
    setLocalFiles((current) => {
      const next = new Map(current);
      for (const { cellId, file } of uploads) next.set(cellId, file);
      return next;
    });
    queueRef.current ??= createMotifUploadQueue();
    queueRef.current.enqueue(uploads);
  }, []);

  useEffect(() => {
    setLocalFiles((current) => {
      const stale = [...current.keys()].filter(
        (cellId) => findCellState(rows, cellId) !== 'uploading'
      );
      if (stale.length === 0) return current;
      const next = new Map(current);
      for (const cellId of stale) next.delete(cellId);
      return next;
    });
  }, [rows]);

  return { localFiles, startUploads };
}
